<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractEvents',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const items = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
  }));

  const $$load_items = async () => {
    return $fn.value
      .fetch('list_contract_protocol', {
        parameters: {
          contract_id: props.contract.id,
          contract_revision_id: props.contract.revision_id,
        },
      })
      .then((d) => _.orderBy(d, ['handover_date'], ['desc']));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'items' }, { name: 'ux' }],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: { items, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_7172922356531116_emit_x2dmd3rja_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_7172922356531116_close_21g24xb3w_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_7172922356531116_showModal_4mrq9dra7_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'HandoverProtocol',
      props: {
        contractId: props.contract.id,
        revisionId: props.contract.revision_id,
        handoverType: 'moving_in',
      },
    };
  };
  const act_5246287230194476_emit_090dbnwxt_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_5246287230194476_close_pkhgk6686_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_5246287230194476_showModal_ftd0v5cw0_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'HandoverProtocol',
      props: {
        contractId: props.contract.id,
        revisionId: props.contract.revision_id,
        handoverType: 'moving_out',
      },
    };
  };
  const inst_evh_2002045350107722_close_m52mk43jd_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_2002045350107722_showModal_2nd662wo8_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'HandoverProtocol',
      props: {
        oid: $event.scope.item.id,
      },
    };
  };

  const act_7315092382398562_reloadComponentData_lp6muetw2 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_f04rzobt9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_lp6muetw2,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_f04rzobt9],
    event: 'reload',
  });
  const act_7172922356531116_emit_x2dmd3rja = new actionTypes.EmitAction({
    actionArgs: act_7172922356531116_emit_x2dmd3rja_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_7172922356531116_close_21g24xb3w =
    new actionTypes.EventHandlerImpl({
      action: act_7172922356531116_emit_x2dmd3rja,
      event: 'close',
      when: inst_evh_7172922356531116_close_21g24xb3w_getWhen,
      displayName: 'emitReload',
    });
  const act_7172922356531116_showModal_4mrq9dra7 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7172922356531116_showModal_4mrq9dra7_getActionArgs,
      events: [inst_evh_7172922356531116_close_21g24xb3w],
    });
  const inst_evh_7172922356531116_clickPreventStop_sj386696c =
    new actionTypes.EventHandlerImpl({
      action: act_7172922356531116_showModal_4mrq9dra7,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_7172922356531116_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7172922356531116_clickPreventStop_sj386696c],
      event: 'click.prevent.stop',
    });
  const act_5246287230194476_emit_090dbnwxt = new actionTypes.EmitAction({
    actionArgs: act_5246287230194476_emit_090dbnwxt_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_5246287230194476_close_pkhgk6686 =
    new actionTypes.EventHandlerImpl({
      action: act_5246287230194476_emit_090dbnwxt,
      event: 'close',
      when: inst_evh_5246287230194476_close_pkhgk6686_getWhen,
      displayName: 'emitReload',
    });
  const act_5246287230194476_showModal_ftd0v5cw0 =
    new actionTypes.ShowModalAction({
      actionArgs: act_5246287230194476_showModal_ftd0v5cw0_getActionArgs,
      events: [inst_evh_5246287230194476_close_pkhgk6686],
    });
  const inst_evh_5246287230194476_clickPreventStop_jnqymfeas =
    new actionTypes.EventHandlerImpl({
      action: act_5246287230194476_showModal_ftd0v5cw0,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_5246287230194476_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5246287230194476_clickPreventStop_jnqymfeas],
      event: 'click.prevent.stop',
    });
  const act_2002045350107722_reloadComponentData_vas2wr3qu =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2002045350107722_close_m52mk43jd =
    new actionTypes.EventHandlerImpl({
      action: act_2002045350107722_reloadComponentData_vas2wr3qu,
      event: 'close',
      when: inst_evh_2002045350107722_close_m52mk43jd_getWhen,
      displayName: 'reloadComponentData',
    });
  const act_2002045350107722_showModal_2nd662wo8 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2002045350107722_showModal_2nd662wo8_getActionArgs,
      events: [inst_evh_2002045350107722_close_m52mk43jd],
    });
  const inst_evh_2002045350107722_clickPreventStop_sgq2rmp8o =
    new actionTypes.EventHandlerImpl({
      action: act_2002045350107722_showModal_2nd662wo8,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2002045350107722_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2002045350107722_clickPreventStop_sgq2rmp8o],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_f04rzobt9,
    inst_evh_7315092382398562_reload,
    inst_evh_7172922356531116_close_21g24xb3w,
    emitReload: inst_evh_7172922356531116_close_21g24xb3w,
    inst_evh_7172922356531116_clickPreventStop_sj386696c,
    inst_evh_7172922356531116_clickPreventStop,
    inst_evh_5246287230194476_close_pkhgk6686,
    inst_evh_5246287230194476_clickPreventStop_jnqymfeas,
    inst_evh_5246287230194476_clickPreventStop,
    inst_evh_2002045350107722_close_m52mk43jd,
    inst_evh_2002045350107722_clickPreventStop_sgq2rmp8o,
    inst_evh_2002045350107722_clickPreventStop,
  };

  const evh_7315092382398562_reload_f04rzobt9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_f04rzobt9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_close_21g24xb3w = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_close_21g24xb3w.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_clickPreventStop_sj386696c = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7172922356531116_clickPreventStop_sj386696c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5246287230194476_close_pkhgk6686 = async (event, scope) => {
    return await actions.inst_evh_5246287230194476_close_pkhgk6686.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5246287230194476_clickPreventStop_jnqymfeas = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5246287230194476_clickPreventStop_jnqymfeas.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5246287230194476_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_5246287230194476_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2002045350107722_close_m52mk43jd = async (event, scope) => {
    return await actions.inst_evh_2002045350107722_close_m52mk43jd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2002045350107722_clickPreventStop_sgq2rmp8o = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2002045350107722_clickPreventStop_sgq2rmp8o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2002045350107722_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2002045350107722_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Contract', 'Protocols and events') }}
          </strong>
          <BDropdown
            size="sm"
            toggle-class="btn-light border"
            :text="pgettext('Contract', 'New event')"
            class="ms-auto"
            v-if="!readonly"
          >
            <BDropdownItemButton
              @click.prevent.stop="
                evh_7172922356531116_clickPreventStop($event, {})
              "
            >
              <ItpText
                :text="
                  pgettext('Contract', 'New handover protocol (moving in)...')
                "
              >
              </ItpText>
            </BDropdownItemButton>
            <BDropdownItemButton
              @click.prevent.stop="
                evh_5246287230194476_clickPreventStop($event, {})
              "
            >
              <ItpText
                :text="
                  pgettext('Contract', 'New handover protocol (moving out)...')
                "
              >
              </ItpText>
            </BDropdownItemButton>
          </BDropdown>
        </ItpBox>
      </template>
      <BListGroup>
        <BListGroupItem v-if="!items || !items.length">
          <ItpText>
            {{ pgettext('Contract', 'No events have been added yet.') }}
          </ItpText>
        </BListGroupItem>
        <BListGroupItem :key="item.id" v-for="item in items">
          <ItpBox alignItems="center">
            <div class="pe-3">
              <ItpIcon name="fluent:calendar-12-regular" class="align-middle">
              </ItpIcon>
              <span class="align-middle">
                {{ date(item.handover_date) }}
              </span>
            </div>
            <ItpText
              :text="pgettext('HandoverType', item.handover_type, 'Sentence')"
              :strikethrough="item.protocol_status == 'cancelled'"
              class="fw-bold px-3"
            >
            </ItpText>
            <UcStatusBadge :status="item.protocol_status" class="px-3">
            </UcStatusBadge>
            <ItpIcon
              :title="gettext('Locked')"
              name="fa-lock"
              class="px-3"
              v-if="item.locked"
            >
            </ItpIcon>
            <BDropdown
              size="sm"
              toggle-class="btn-default-borderless"
              no-caret
              class="ms-auto mb-auto"
            >
              <template #button-content>
                <ItpIcon fa="ellipsis-v" class="mx-1"> </ItpIcon>
              </template>
              <BDropdownItemButton
                @click.prevent.stop="
                  evh_2002045350107722_clickPreventStop($event, { item })
                "
              >
                <ItpText :text="pgettext('Command', 'Open')"> </ItpText>
              </BDropdownItemButton>
            </BDropdown>
          </ItpBox>
          <table
            class="table table-condensed mt-2 mb-0 pb-0 mx-2"
            v-if="item.protocol_status == 'completed' && item.meter_readings"
          >
            <tr
              :key="reading.serial_number"
              v-for="reading in item.meter_readings"
            >
              <td>
                <UcMeterMiniCard :item="reading" no-strong link-style-underline>
                </UcMeterMiniCard>
              </td>
              <td>{{ number(reading.value) }} {{ reading.unit }}</td>
            </tr>
          </table>
        </BListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
